import React from "react";
import {
  Loader2, LayoutDashboard, RefreshCw, ChevronDown, Eye, Clock, Calendar,
  Send, MessageCircle, UserPlus, Activity, BarChart3, AlertTriangle,
} from "lucide-react";

export const CommandCenter = ({ data, roas, onJumpTab, onRefresh }) => {
  if (!data) {
    return (
      <div className="flex items-center gap-2 text-xs text-[#64748B] py-12 justify-center">
        <Loader2 size={16} className="animate-spin" /> Loading Command Center…
      </div>
    );
  }
  const h = data.headline || {};
  const tiles = [
    { label: "Pending drafts", value: h.pending_drafts, color: "pink", icon: Eye, tab: "queue" },
    { label: "Scheduled (next 7d)", value: h.scheduled_due_7d, color: "violet", icon: Clock, tab: "scheduled" },
    { label: "Festivals due (7d)", value: h.festivals_due_7d, color: "rose", icon: Calendar, tab: "trends" },
    { label: "Retargeting queue", value: h.retargeting_pending, color: "emerald", icon: Send, tab: "retarget" },
    { label: "Inf. replied", value: h.influencers_replied, color: "amber", icon: MessageCircle, tab: "influencers" },
    { label: "Active collabs", value: h.influencers_collab, color: "cyan", icon: UserPlus, tab: "influencers" },
  ];

  const priorityColors = {
    high: "bg-rose-50 border-rose-200 text-rose-900",
    medium: "bg-amber-50 border-amber-200 text-amber-900",
    low: "bg-gray-50 border-gray-200 text-gray-700",
  };

  return (
    <div className="space-y-5" data-testid="doozyad-command-center">
      {/* Next actions */}
      <div className="bg-gradient-to-br from-violet-50 via-pink-50 to-rose-50 border border-violet-200 rounded-2xl p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-bold text-[#1E1B4B] flex items-center gap-2">
            <LayoutDashboard size={16} className="text-violet-600" /> What needs you today
          </h3>
          <button onClick={onRefresh} className="p-1.5 hover:bg-white rounded-lg" data-testid="doozyad-cc-refresh">
            <RefreshCw size={13} />
          </button>
        </div>
        <div className="space-y-1.5">
          {(data.next_actions || []).map((a, i) => (
            <button
              key={`${a.tab}-${a.label}`}
              onClick={() => onJumpTab(a.tab)}
              className={`w-full text-left px-3 py-2 rounded-xl border ${priorityColors[a.priority] || priorityColors.low} flex items-center gap-2 hover:shadow-sm transition-shadow`}
              data-testid={`doozyad-cc-action-${a.tab}-${i}`}
            >
              <span className="text-xs flex-1">{a.label}</span>
              <ChevronDown size={12} className="-rotate-90 opacity-60" />
            </button>
          ))}
        </div>
      </div>

      {/* Tile grid */}
      <div className="grid sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="doozyad-cc-tiles">
        {tiles.map((t) => (
          <button
            key={t.label}
            onClick={() => onJumpTab(t.tab)}
            className={`p-3 rounded-xl border bg-white hover:shadow-md transition-shadow text-left`}
          >
            <div className={`flex items-center gap-1.5 text-xs text-[#64748B] mb-1`}>
              <t.icon size={12} className={`text-${t.color}-500`} /> {t.label}
            </div>
            <div className="text-2xl font-bold text-[#1E1B4B]">{t.value || 0}</div>
          </button>
        ))}
      </div>

      {/* Reach + Impressions banner */}
      <div className="bg-white border border-gray-200 rounded-2xl p-5 grid sm:grid-cols-3 gap-4">
        <div>
          <div className="text-[11px] uppercase tracking-wider text-[#64748B] mb-1">Last 7 days</div>
          <div className="text-3xl font-bold text-[#DC283C]">{(h.total_reach_7d || 0).toLocaleString("en-IN")}</div>
          <div className="text-xs text-[#64748B]">organic reach</div>
        </div>
        <div>
          <div className="text-[11px] uppercase tracking-wider text-[#64748B] mb-1">Impressions</div>
          <div className="text-3xl font-bold text-violet-700">{(h.total_impressions_7d || 0).toLocaleString("en-IN")}</div>
          <div className="text-xs text-[#64748B]">{h.published_7d || 0} posts published</div>
        </div>
        <div>
          <div className="text-[11px] uppercase tracking-wider text-[#64748B] mb-1">Pipeline</div>
          <div className="text-3xl font-bold text-emerald-600">{(h.scheduled_total || 0)}</div>
          <div className="text-xs text-[#64748B]">total drafts scheduled</div>
        </div>
      </div>

      {/* GA4 ROAS — render based on status */}
      <RoasBlock roas={roas} />

      {/* Festivals due */}
      {data.festivals_due && data.festivals_due.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-2xl p-5">
          <h3 className="text-sm font-bold text-[#1E1B4B] flex items-center gap-2 mb-3">
            <Calendar size={14} /> Festivals on the horizon
          </h3>
          <div className="grid sm:grid-cols-2 gap-2">
            {data.festivals_due.map((f) => (
              <div key={f.id} className="px-3 py-2 bg-rose-50 border border-rose-100 rounded-lg flex justify-between items-center">
                <span className="text-sm font-semibold text-[#1E1B4B]">{f.name}</span>
                <span className="text-[11px] font-bold text-rose-700">T-{f.days_until}d · {f.date}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Top published */}
      {data.top_published_7d && data.top_published_7d.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-2xl p-5">
          <h3 className="text-sm font-bold text-[#1E1B4B] flex items-center gap-2 mb-3">
            <Activity size={14} /> Top performers (last 7d)
          </h3>
          <div className="space-y-2">
            {data.top_published_7d.map((p) => (
              <div key={p.draft_id} className="flex items-start justify-between gap-3 pb-2 border-b border-gray-100 last:border-0">
                <div className="flex-1">
                  <div className="text-xs font-semibold text-[#DC283C]">{p.theme_name}</div>
                  <div className="text-xs text-[#64748B] line-clamp-1">{(p.selected_caption || "").slice(0, 90)}</div>
                </div>
                <div className="text-right">
                  <div className="text-sm font-bold text-emerald-600">{((p.insights || {}).reach || 0).toLocaleString("en-IN")}</div>
                  <div className="text-[10px] text-[#94A3B8]">reach</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const RoasBlock = ({ roas }) => {
  if (!roas) {
    return (
      <div className="bg-white border border-gray-200 rounded-2xl p-5 text-xs text-[#64748B] flex items-center gap-2">
        <Loader2 size={13} className="animate-spin" /> Loading GA4 ROAS…
      </div>
    );
  }
  if (roas.status === "ok") {
    const t = roas.totals || {};
    return (
      <div className="bg-gradient-to-br from-emerald-50 to-cyan-50 border border-emerald-200 rounded-2xl p-5" data-testid="doozyad-cc-roas-ok">
        <h3 className="text-sm font-bold text-[#1E1B4B] flex items-center gap-2 mb-3">
          <BarChart3 size={14} className="text-emerald-600" /> GA4 ROAS · last 7 days · doozyad_* campaigns
        </h3>
        <div className="grid sm:grid-cols-4 gap-3 mb-4">
          <div className="bg-white rounded-lg p-3"><div className="text-[10px] uppercase tracking-wider text-[#64748B]">Sessions</div><div className="text-2xl font-bold text-[#1E1B4B]">{(t.sessions || 0).toLocaleString("en-IN")}</div></div>
          <div className="bg-white rounded-lg p-3"><div className="text-[10px] uppercase tracking-wider text-[#64748B]">Conversions</div><div className="text-2xl font-bold text-emerald-600">{Math.round(t.conversions || 0)}</div></div>
          <div className="bg-white rounded-lg p-3"><div className="text-[10px] uppercase tracking-wider text-[#64748B]">Engagement</div><div className="text-2xl font-bold text-violet-600">{((t.engagement_rate || 0) * 100).toFixed(1)}%</div></div>
          <div className="bg-white rounded-lg p-3"><div className="text-[10px] uppercase tracking-wider text-[#64748B]">Revenue</div><div className="text-2xl font-bold text-amber-600">₹{(t.revenue || 0).toLocaleString("en-IN")}</div></div>
        </div>
        <div className="text-[11px] text-[#64748B]">Top campaigns (by sessions):</div>
        <div className="mt-1 space-y-1 max-h-40 overflow-y-auto">
          {(roas.rows || []).slice(0, 8).map((row) => (
            <div key={`${row.campaign}-${row.source}`} className="text-xs flex items-center gap-2">
              <span className="w-48 truncate text-[#1E1B4B]">{row.campaign}</span>
              <span className="w-12 text-[#64748B]">{row.source}</span>
              <span className="ml-auto font-bold text-emerald-600">{row.sessions}</span>
            </div>
          ))}
        </div>
      </div>
    );
  }
  // not_configured / creds_file_missing / error → setup guide
  const guide = roas.setup_guide || {};
  return (
    <details className="bg-amber-50 border border-amber-200 rounded-2xl p-5" data-testid="doozyad-cc-roas-setup">
      <summary className="cursor-pointer text-sm font-bold text-amber-900 flex items-center gap-2">
        <AlertTriangle size={14} /> GA4 ROAS · {roas.status === "not_configured" ? "not configured yet" : roas.status} — click to set up
      </summary>
      <div className="mt-3 text-xs text-amber-900">
        <p className="mb-2"><b>{guide.title || "Connect GA4"}</b></p>
        <p className="mb-3 text-amber-800">{guide.why || ""}</p>
        <ol className="list-decimal pl-5 space-y-1">
          {(guide.steps || []).map((step) => <li key={step}>{step}</li>)}
        </ol>
        {roas.error && <p className="mt-3 text-rose-700 text-[11px]"><b>Error:</b> {roas.error}</p>}
      </div>
    </details>
  );
};
